import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, ChevronRight, ChevronDown, Home, Users, Calendar, 
  DollarSign, Package, ClipboardCheck, MapPin, Trophy, Dumbbell,
  History, Settings, LogIn, UserPlus, Search, Filter, Plus,
  Edit, Trash2, Eye, FileText, Upload, Download, Bell, Check,
  AlertTriangle, Info, HelpCircle, ArrowLeft, Menu, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const Manual = () => {
  const [activeSection, setActiveSection] = useState('inicio');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const sections = [
    { id: 'inicio', title: 'Inicio', icon: Home },
    { id: 'login', title: 'Acceso al Sistema', icon: LogIn },
    { id: 'dashboard', title: 'Dashboard', icon: Home },
    { id: 'jugadores', title: 'Gestión de Jugadores', icon: Users },
    { id: 'entrenamientos', title: 'Entrenamientos', icon: Dumbbell },
    { id: 'partidos', title: 'Partidos', icon: Trophy },
    { id: 'asistencia', title: 'Control de Asistencia', icon: ClipboardCheck },
    { id: 'finanzas', title: 'Finanzas', icon: DollarSign },
    { id: 'materiales', title: 'Materiales', icon: Package },
    { id: 'calendario', title: 'Calendario', icon: Calendar },
    { id: 'locaciones', title: 'Locaciones', icon: MapPin },
    { id: 'usuarios', title: 'Usuarios', icon: Shield },
    { id: 'historial', title: 'Historial', icon: History },
    { id: 'roles', title: 'Roles y Permisos', icon: Settings },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'inicio':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Bienvenido al Manual de Usuario</h2>
            <p className="text-slate-300 mb-6">
              Este manual te guiará a través de todas las funcionalidades de Sport Control, 
              el sistema integral de gestión para clubes de fútbol.
            </p>
            
            <div className="bg-brand-500/10 border border-brand-500/20 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-brand-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-brand-300 mb-1">Navegación del Manual</h4>
                  <p className="text-sm text-slate-400">
                    Utiliza el menú lateral para navegar entre las diferentes secciones. 
                    Cada sección contiene instrucciones detalladas sobre cómo usar esa funcionalidad.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-white mb-3">Contenido del Manual</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {sections.slice(1).map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-700/50 transition-colors text-left"
                >
                  <section.icon className="w-5 h-5 text-brand-400" />
                  <span className="text-slate-300">{section.title}</span>
                  <ChevronRight className="w-4 h-4 text-slate-500 ml-auto" />
                </button>
              ))}
            </div>
          </div>
        );

      case 'login':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Acceso al Sistema</h2>
            <p className="text-slate-300 mb-6">
              Para acceder a Sport Control, necesitarás credenciales proporcionadas por un administrador.
            </p>

            <h3 className="text-lg font-semibold text-white mb-3">Pasos para iniciar sesión</h3>
            <ol className="space-y-4 mb-6">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">1</span>
                <div>
                  <p className="text-slate-300">Ingresa a la URL del sistema</p>
                  <p className="text-sm text-slate-500">Se mostrará la pantalla de login</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">2</span>
                <div>
                  <p className="text-slate-300">Ingresa tu nombre de usuario</p>
                  <p className="text-sm text-slate-500">El usuario es asignado por el administrador</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">3</span>
                <div>
                  <p className="text-slate-300">Ingresa tu contraseña</p>
                  <p className="text-sm text-slate-500">Las contraseñas distinguen mayúsculas y minúsculas</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">4</span>
                <div>
                  <p className="text-slate-300">Haz clic en "Iniciar Sesión"</p>
                  <p className="text-sm text-slate-500">Serás redirigido según tu rol de usuario</p>
                </div>
              </li>
            </ol>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-300 mb-1">Credenciales de prueba</h4>
                  <p className="text-sm text-slate-400 mb-2">Para ambientes de demostración:</p>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li><strong>Admin:</strong> admin / admin123</li>
                    <li><strong>Scout:</strong> scout1 / scout123</li>
                    <li><strong>Coach:</strong> coach1 / coach123</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'dashboard':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Dashboard</h2>
            <p className="text-slate-300 mb-6">
              El Dashboard es la pantalla principal que muestra un resumen de toda la actividad del club.
            </p>

            <h3 className="text-lg font-semibold text-white mb-3">Componentes del Dashboard</h3>
            
            <div className="space-y-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-brand-300 mb-2">Acciones Rápidas</h4>
                <p className="text-sm text-slate-400">
                  Botones de acceso directo a las acciones más frecuentes: ver jugadores, 
                  crear práctica, crear partido, pasar lista y registrar movimiento financiero.
                </p>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-brand-300 mb-2">Estadísticas Generales</h4>
                <p className="text-sm text-slate-400">
                  Tarjetas con métricas clave: total de jugadores, equipos (categorías), 
                  prácticas y partidos de los últimos 7 días, canchas y stock de materiales.
                </p>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-brand-300 mb-2">Próximos Eventos</h4>
                <p className="text-sm text-slate-400">
                  Lista de los próximos entrenamientos y partidos programados, 
                  mostrando la categoría y fecha de cada evento.
                </p>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-brand-300 mb-2">Finanzas del Mes</h4>
                <p className="text-sm text-slate-400">
                  Resumen financiero con balance actual, ingresos y egresos del mes, 
                  junto con los últimos movimientos registrados.
                </p>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-brand-300 mb-2">Alertas</h4>
                <p className="text-sm text-slate-400">
                  Notificaciones importantes sobre materiales con stock bajo 
                  y certificados de jugadores próximos a vencer o ya vencidos.
                </p>
              </div>
            </div>
          </div>
        );

      case 'jugadores':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Gestión de Jugadores</h2>
            <p className="text-slate-300 mb-6">
              El módulo de jugadores permite administrar toda la información del plantel, 
              incluyendo datos personales, evaluaciones y documentación.
            </p>

            <h3 className="text-lg font-semibold text-white mb-3">Funcionalidades principales</h3>
            
            <div className="space-y-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Plus className="w-4 h-4 text-brand-400" />
                  <h4 className="font-medium text-brand-300">Crear Jugador</h4>
                </div>
                <p className="text-sm text-slate-400 mb-2">
                  Haz clic en "Nuevo Jugador" para abrir el formulario de registro.
                </p>
                <ul className="text-sm text-slate-400 list-disc list-inside space-y-1">
                  <li>Datos personales (nombre, documento, fecha de nacimiento)</li>
                  <li>Contacto (teléfono, email, dirección)</li>
                  <li>Datos deportivos (categoría, posición, pie preferido)</li>
                  <li>Evaluaciones técnicas, tácticas, físicas y psicológicas</li>
                  <li>Foto de perfil</li>
                </ul>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Search className="w-4 h-4 text-brand-400" />
                  <h4 className="font-medium text-brand-300">Buscar y Filtrar</h4>
                </div>
                <p className="text-sm text-slate-400">
                  Utiliza la barra de búsqueda para encontrar jugadores por nombre o documento. 
                  Los filtros permiten segmentar por categoría, posición, estado y nivel de índice.
                </p>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-brand-400" />
                  <h4 className="font-medium text-brand-300">Ver Detalle</h4>
                </div>
                <p className="text-sm text-slate-400">
                  Haz clic en un jugador para ver su ficha completa. Incluye todas las evaluaciones, 
                  notas, documentos adjuntos, tests realizados e historial de asistencia.
                </p>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Upload className="w-4 h-4 text-brand-400" />
                  <h4 className="font-medium text-brand-300">Importación Masiva</h4>
                </div>
                <p className="text-sm text-slate-400">
                  El botón "Importar Excel" permite cargar múltiples jugadores desde una planilla. 
                  El sistema validará los datos y mostrará errores si los hay.
                </p>
              </div>
            </div>

            <div className="bg-brand-500/10 border border-brand-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-brand-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-brand-300 mb-1">Jugadores Pendientes</h4>
                  <p className="text-sm text-slate-400">
                    Los jugadores creados por scouts quedan en estado "Pendiente" hasta que 
                    un administrador los valide. El icono de campana en el header muestra 
                    la cantidad de jugadores pendientes de validación.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'entrenamientos':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Entrenamientos</h2>
            <p className="text-slate-300 mb-6">
              Gestiona las sesiones de entrenamiento del club, asignando categorías, 
              locaciones y controlando la asistencia.
            </p>

            <h3 className="text-lg font-semibold text-white mb-3">Crear un entrenamiento</h3>
            <ol className="space-y-3 mb-6">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">1</span>
                <p className="text-slate-300">Haz clic en "Nueva Práctica"</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">2</span>
                <p className="text-slate-300">Selecciona la categoría que entrenará</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">3</span>
                <p className="text-slate-300">Elige la fecha y hora del entrenamiento</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">4</span>
                <p className="text-slate-300">Selecciona la locación (cancha)</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">5</span>
                <p className="text-slate-300">Opcionalmente agrega notas o descripción</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">6</span>
                <p className="text-slate-300">Guarda el entrenamiento</p>
              </li>
            </ol>

            <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-brand-300 mb-2">Indicador de Asistencia</h4>
              <p className="text-sm text-slate-400">
                Los entrenamientos muestran un badge "Cerrado" cuando ya se ha registrado 
                la asistencia. Esto permite identificar rápidamente cuáles aún están pendientes.
              </p>
            </div>
          </div>
        );

      case 'partidos':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Partidos</h2>
            <p className="text-slate-300 mb-6">
              Registra los partidos del club con sistema de convocatoria de jugadores 
              y seguimiento de resultados.
            </p>

            <h3 className="text-lg font-semibold text-white mb-3">Crear un partido</h3>
            <ol className="space-y-3 mb-6">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">1</span>
                <p className="text-slate-300">Haz clic en "Nuevo Partido"</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">2</span>
                <p className="text-slate-300">Selecciona la categoría que jugará</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">3</span>
                <p className="text-slate-300">Ingresa el nombre del rival</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">4</span>
                <p className="text-slate-300">Selecciona fecha, hora y locación</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">5</span>
                <p className="text-slate-300">Indica si es local o visitante</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">6</span>
                <p className="text-slate-300"><strong>Selecciona los jugadores convocados</strong> (importante)</p>
              </li>
            </ol>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-300 mb-1">Convocatoria de Jugadores</h4>
                  <p className="text-sm text-slate-400">
                    Solo los jugadores que selecciones como "convocados" aparecerán en la 
                    lista de asistencia del partido. Asegúrate de convocar a todos los 
                    jugadores que asistirán.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-lg p-4">
              <h4 className="font-medium text-brand-300 mb-2">Registrar Resultado</h4>
              <p className="text-sm text-slate-400">
                Una vez jugado el partido, edita el registro para agregar el marcador final. 
                El sistema mostrará si fue victoria, empate o derrota.
              </p>
            </div>
          </div>
        );

      case 'asistencia':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Control de Asistencia</h2>
            <p className="text-slate-300 mb-6">
              Registra la asistencia de los jugadores tanto en entrenamientos como en partidos.
            </p>

            <h3 className="text-lg font-semibold text-white mb-3">Pasar lista</h3>
            <ol className="space-y-3 mb-6">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">1</span>
                <p className="text-slate-300">Selecciona el tipo de evento (Entrenamiento o Partido)</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">2</span>
                <p className="text-slate-300">Elige el evento específico de la lista</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">3</span>
                <p className="text-slate-300">Se mostrarán los jugadores de la categoría (o convocados en partidos)</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">4</span>
                <p className="text-slate-300">Marca cada jugador como Presente, Ausente o Tardanza</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">5</span>
                <p className="text-slate-300">Guarda la asistencia</p>
              </li>
            </ol>

            <div className="bg-slate-800/50 rounded-lg p-4">
              <h4 className="font-medium text-brand-300 mb-2">Estados de Asistencia</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  <span className="text-slate-300"><strong>Presente:</strong> El jugador asistió al evento</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                  <span className="text-slate-300"><strong>Ausente:</strong> El jugador no asistió</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-amber-500 rounded-full"></span>
                  <span className="text-slate-300"><strong>Tardanza:</strong> El jugador llegó tarde</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'finanzas':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Finanzas</h2>
            <p className="text-slate-300 mb-6">
              Control financiero del club con registro de ingresos, egresos y balance en tiempo real.
            </p>

            <h3 className="text-lg font-semibold text-white mb-3">Registrar Movimiento</h3>
            <ol className="space-y-3 mb-6">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">1</span>
                <p className="text-slate-300">Haz clic en "Nuevo Movimiento"</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">2</span>
                <p className="text-slate-300">Selecciona el tipo: Ingreso o Egreso</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">3</span>
                <p className="text-slate-300">Elige una categoría (cuota, sueldo, material, etc.)</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">4</span>
                <p className="text-slate-300">Ingresa el monto y una descripción</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">5</span>
                <p className="text-slate-300">Guarda el movimiento</p>
              </li>
            </ol>

            <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-brand-300 mb-2">Categorías Financieras</h4>
              <p className="text-sm text-slate-400">
                Las categorías se gestionan desde el menú Configuración → Categorías Finanzas. 
                Puedes crear categorías personalizadas para organizar mejor los movimientos.
              </p>
            </div>

            <div className="bg-slate-800/50 rounded-lg p-4">
              <h4 className="font-medium text-brand-300 mb-2">Balance</h4>
              <p className="text-sm text-slate-400">
                El sistema calcula automáticamente el balance sumando todos los ingresos 
                y restando todos los egresos. Se muestra en la parte superior de la página.
              </p>
            </div>
          </div>
        );

      case 'materiales':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Materiales</h2>
            <p className="text-slate-300 mb-6">
              Inventario de equipamiento deportivo con control de stock y alertas automáticas.
            </p>

            <h3 className="text-lg font-semibold text-white mb-3">Agregar Material</h3>
            <ol className="space-y-3 mb-6">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">1</span>
                <p className="text-slate-300">Haz clic en "Nuevo Material"</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">2</span>
                <p className="text-slate-300">Ingresa el nombre del material (ej: "Pelota de fútbol")</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">3</span>
                <p className="text-slate-300">Selecciona una categoría</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">4</span>
                <p className="text-slate-300">Ingresa la cantidad actual en stock</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">5</span>
                <p className="text-slate-300">Define el stock mínimo para alertas</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">6</span>
                <p className="text-slate-300">Opcionalmente sube una foto (arrastra y suelta)</p>
              </li>
            </ol>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Bell className="w-5 h-5 text-amber-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-300 mb-1">Alertas de Stock Bajo</h4>
                  <p className="text-sm text-slate-400">
                    Cuando un material tiene cantidad igual o menor al stock mínimo configurado, 
                    aparecerá en las alertas del Dashboard y en el icono de campana del header.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'calendario':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Calendario</h2>
            <p className="text-slate-300 mb-6">
              Vista centralizada de todos los eventos del club: entrenamientos y partidos.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-brand-300 mb-2">Navegación</h4>
                <p className="text-sm text-slate-400">
                  Usa las flechas para moverte entre meses. El botón "Hoy" te lleva 
                  al mes actual. Puedes filtrar por categoría usando el selector.
                </p>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-brand-300 mb-2">Código de Colores</h4>
                <div className="space-y-2 text-sm mt-2">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                    <span className="text-slate-300">Entrenamientos</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                    <span className="text-slate-300">Partidos</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-brand-300 mb-2">Ver Detalles</h4>
                <p className="text-sm text-slate-400">
                  Haz clic en cualquier evento del calendario para ver sus detalles 
                  o para acceder rápidamente a pasar lista.
                </p>
              </div>
            </div>
          </div>
        );

      case 'locaciones':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Locaciones</h2>
            <p className="text-slate-300 mb-6">
              Administra las canchas y espacios de entrenamiento del club.
            </p>

            <h3 className="text-lg font-semibold text-white mb-3">Agregar Locación</h3>
            <ol className="space-y-3 mb-6">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">1</span>
                <p className="text-slate-300">Haz clic en "Nueva Locación"</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">2</span>
                <p className="text-slate-300">Ingresa el nombre (ej: "Cancha Principal")</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">3</span>
                <p className="text-slate-300">Ingresa la dirección completa</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">4</span>
                <p className="text-slate-300">El sistema geocodificará automáticamente las coordenadas</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">5</span>
                <p className="text-slate-300">Guarda la locación</p>
              </li>
            </ol>

            <div className="bg-slate-800/50 rounded-lg p-4">
              <h4 className="font-medium text-brand-300 mb-2">Mapa</h4>
              <p className="text-sm text-slate-400">
                La sección "Mapa" en Herramientas muestra todas las locaciones y jugadores 
                en un mapa interactivo, útil para planificar logística.
              </p>
            </div>
          </div>
        );

      case 'usuarios':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Gestión de Usuarios</h2>
            <p className="text-slate-300 mb-6">
              Administra los usuarios que tienen acceso al sistema (solo administradores).
            </p>

            <h3 className="text-lg font-semibold text-white mb-3">Crear Usuario</h3>
            <ol className="space-y-3 mb-6">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">1</span>
                <p className="text-slate-300">Haz clic en "Nuevo Usuario"</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">2</span>
                <p className="text-slate-300">Ingresa un nombre de usuario único</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">3</span>
                <p className="text-slate-300">Crea una contraseña segura</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">4</span>
                <p className="text-slate-300">Selecciona el rol: Admin, User, Scout o Coach</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-sm font-bold text-white">5</span>
                <p className="text-slate-300">Guarda el usuario</p>
              </li>
            </ol>
          </div>
        );

      case 'historial':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Historial de Actividad</h2>
            <p className="text-slate-300 mb-6">
              Registro de todas las acciones realizadas en el sistema para auditoría.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-brand-300 mb-2">¿Qué se registra?</h4>
                <ul className="text-sm text-slate-400 list-disc list-inside space-y-1">
                  <li>Creación, edición y eliminación de jugadores</li>
                  <li>Movimientos financieros</li>
                  <li>Cambios en materiales</li>
                  <li>Entrenamientos y partidos</li>
                  <li>Registro de asistencias</li>
                </ul>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-brand-300 mb-2">Filtros Disponibles</h4>
                <p className="text-sm text-slate-400">
                  Puedes filtrar el historial por módulo (Jugadores, Finanzas, etc.), 
                  por tipo de acción (crear, editar, eliminar) y por usuario.
                </p>
              </div>
            </div>
          </div>
        );

      case 'roles':
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Roles y Permisos</h2>
            <p className="text-slate-300 mb-6">
              El sistema tiene roles predefinidos con diferentes niveles de acceso.
            </p>

            <div className="space-y-4">
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <h4 className="font-medium text-amber-300 mb-2">Administrador (admin)</h4>
                <p className="text-sm text-slate-400 mb-2">Acceso completo a todas las funciones:</p>
                <ul className="text-sm text-slate-400 list-disc list-inside space-y-1">
                  <li>Gestión de usuarios</li>
                  <li>Control financiero completo</li>
                  <li>Validación de jugadores</li>
                  <li>Configuración del sistema</li>
                  <li>Todas las demás funciones</li>
                </ul>
              </div>
              
              <div className="bg-brand-500/10 border border-brand-500/20 rounded-lg p-4">
                <h4 className="font-medium text-brand-300 mb-2">Usuario (user)</h4>
                <p className="text-sm text-slate-400 mb-2">Acceso operativo sin administración:</p>
                <ul className="text-sm text-slate-400 list-disc list-inside space-y-1">
                  <li>Gestión de jugadores</li>
                  <li>Entrenamientos y partidos</li>
                  <li>Materiales</li>
                  <li>Calendario y locaciones</li>
                  <li>Sin acceso a usuarios ni configuración avanzada</li>
                </ul>
              </div>
              
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                <h4 className="font-medium text-purple-300 mb-2">Scout</h4>
                <p className="text-sm text-slate-400 mb-2">Enfocado en captación de jugadores:</p>
                <ul className="text-sm text-slate-400 list-disc list-inside space-y-1">
                  <li>Agregar nuevos jugadores (quedan pendientes)</li>
                  <li>Ver mapa de jugadores</li>
                  <li>Editar jugadores que él creó</li>
                  <li>Sin acceso a finanzas ni configuración</li>
                </ul>
              </div>
              
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                <h4 className="font-medium text-green-300 mb-2">Entrenador (coach)</h4>
                <p className="text-sm text-slate-400 mb-2">Enfocado en el día a día deportivo:</p>
                <ul className="text-sm text-slate-400 list-disc list-inside space-y-1">
                  <li>Control de asistencia</li>
                  <li>Ver entrenamientos y partidos</li>
                  <li>Consultar jugadores</li>
                  <li>Sin acceso a edición ni configuración</li>
                </ul>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/about" className="flex items-center gap-3">
                <img 
                  src={`${process.env.PUBLIC_URL}/logos/LogoSC_Color_Fondos_oscuros.svg`} 
                  alt="Sport Control" 
                  className="h-10 w-auto"
                />
                <div>
                  <p className="text-xs text-slate-400">Manual de Usuario</p>
                </div>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/about">
                <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-700/50 hidden sm:flex">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver
                </Button>
              </Link>
              <Link to="/login">
                <Button className="bg-brand-500 hover:bg-brand-600 text-white">
                  Iniciar Sesión
                </Button>
              </Link>
              <button 
                className="lg:hidden p-2 text-slate-300 hover:text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="pt-16 flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:block w-72 fixed left-0 top-16 bottom-0 bg-slate-800/30 border-r border-slate-700/50 overflow-y-auto">
          <nav className="p-4 space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all ${
                  activeSection === section.id
                    ? 'bg-brand-500/20 text-brand-300 font-medium'
                    : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-200'
                }`}
              >
                <section.icon className="w-4 h-4" />
                {section.title}
              </button>
            ))}
          </nav>
        </aside>

        {/* Mobile Sidebar */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-40 pt-16">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-72 bg-slate-800 pt-16 overflow-y-auto">
              <nav className="p-4 space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => {
                      setActiveSection(section.id);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all ${
                      activeSection === section.id
                        ? 'bg-brand-500/20 text-brand-300 font-medium'
                        : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-200'
                    }`}
                  >
                    <section.icon className="w-4 h-4" />
                    {section.title}
                  </button>
                ))}
              </nav>
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 lg:ml-72 p-6 sm:p-8">
          <div className="max-w-4xl">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Manual;
